/* src/modules/peer/AppreciationWall.js */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
    collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, 
    doc, increment, getDocs, where, arrayUnion, arrayRemove, writeBatch 
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import confetti from 'canvas-confetti';
import { 
    PlusIcon, 
    XCircleIcon, 
    SparklesIcon,
    TrashIcon,
    TrophyIcon // ✅ Added Trophy Icon
} from '../../components/icons/Icons';
import AchievementCard from './AchievementCard';

// --- CONFIGURATION ---
const BRAND_COLORS = {
    darkBlue: '#213368',
    teal: '#20BEC6',
    lightTeal: '#C9E9E6',
    lightGrey: '#EFEFEF',
    orange: '#F4A866',
    red: '#D95448'
};

const REACTIONS = [
    { icon: '❤️', label: 'Love' },
    { icon: '👏', label: 'Clap' },
    { icon: '🔥', label: 'Fire' },
    { icon: '🎉', label: 'Party' }
];

const ICONS = {
    commitment: <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="6"></circle><circle cx="12" cy="12" r="2"></circle></svg>,
    collaboration: <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
    customer: <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
};

const VALUES = {
    commitment: { 
        label: 'Commitment', 
        description: 'For dedication, passion, and taking ownership.',
        subValues: ['Dedication', 'Passion', 'Ownership'], 
        color: BRAND_COLORS.orange,
        bg: '#FFF3E0', 
        icon: ICONS.commitment
    },
    collaboration: { 
        label: 'Collaboration', 
        description: 'For exceptional teamwork and knowledge sharing.',
        subValues: ['Teamwork', 'Trust', 'Knowledge Sharing'], 
        color: BRAND_COLORS.teal,
        bg: BRAND_COLORS.lightTeal, 
        icon: ICONS.collaboration
    },
    customer: { 
        label: 'Customer Orientation', 
        description: 'For creating service excellence and empathy.',
        subValues: ['Empathy', 'Service Excellence', 'Innovation'], 
        color: BRAND_COLORS.red,
        bg: '#FEE2E2', 
        icon: ICONS.customer
    }
};

// --- HELPER: User Avatar ---
const UserAvatar = ({ name, photoUrl, size = 'md' }) => {
    const sizeClasses = size === 'lg' ? 'h-16 w-16 text-2xl' : 'h-12 w-12 text-lg'; 
    if (photoUrl && photoUrl.length > 10) {
        return <img src={photoUrl} alt={name} className={`${sizeClasses} rounded-full object-cover border border-gray-200 shadow-sm flex-shrink-0`} referrerPolicy="no-referrer" onError={(e) => { e.target.style.display = 'none'; }} />;
    }
    return <div className={`${sizeClasses} rounded-full flex items-center justify-center font-bold shadow-sm flex-shrink-0`} style={{ backgroundColor: BRAND_COLORS.lightTeal, color: BRAND_COLORS.darkBlue }}>{name?.charAt(0).toUpperCase() || 'U'}</div>;
};

// --- HELPER: Date Formatter ---
const formatDateTime = (val) => {
    if (!val) return '-';
    try {
        const date = val.toDate ? val.toDate() : new Date(val);
        return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true }).format(date);
    } catch (e) { return '-'; }
};

// ===================================================================
// ## MAIN COMPONENT
// ===================================================================
export default function AppreciationWall({ userProfile, appId }) {
    const [appreciations, setAppreciations] = useState([]);
    const [users, setUsers] = useState([]); 
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [viewingUser, setViewingUser] = useState(null);

    // --- Filters ---
    const [searchQuery, setSearchQuery] = useState('');
    const [timeFilter, setTimeFilter] = useState('All'); 
    const [valueFilter, setValueFilter] = useState('All'); 
    const [visibleCount, setVisibleCount] = useState(10); 

    useEffect(() => {
        const appRef = collection(db, `artifacts/${appId}/appreciations`);
        const qApp = query(appRef, orderBy('createdAt', 'desc'));
        
        const unsubApp = onSnapshot(qApp, (snapshot) => {
            setAppreciations(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoading(false);
        });

        const fetchUsers = async () => {
            const uRef = collection(db, `artifacts/${appId}/users`);
            const qU = query(uRef, where('status', '==', 'Approved'));
            const snap = await getDocs(qU);
            setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        };
        fetchUsers();

        return () => unsubApp();
    }, [appId]);

    const userMap = useMemo(() => {
        const map = {};
        users.forEach(u => { map[u.id] = u; });
        return map;
    }, [users]);

    // --- FILTER LOGIC ---
    const filteredAppreciations = useMemo(() => {
        let filtered = [...appreciations];

        if (searchQuery) {
            const lowerQ = searchQuery.toLowerCase();
            filtered = filtered.filter(item => 
                item.toUserName?.toLowerCase().includes(lowerQ) || 
                item.fromUserName?.toLowerCase().includes(lowerQ) ||
                item.message?.toLowerCase().includes(lowerQ)
            );
        }

        if (valueFilter !== 'All') {
            filtered = filtered.filter(item => item.valueCategory === valueFilter);
        }

        if (timeFilter !== 'All') {
            const now = new Date();
            const itemDate = (item) => item.createdAt?.toDate ? item.createdAt.toDate() : new Date(item.createdAt);
            
            filtered = filtered.filter(item => {
                const date = itemDate(item);
                if (timeFilter === 'Today') return date.toDateString() === now.toDateString();
                if (timeFilter === 'This Week') {
                    const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
                    return date >= startOfWeek;
                }
                if (timeFilter === 'This Month') return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                if (timeFilter === 'This Year') return date.getFullYear() === now.getFullYear();
                return true;
            });
        }

        return filtered;
    }, [appreciations, searchQuery, timeFilter, valueFilter]);

    const visibleAppreciations = filteredAppreciations.slice(0, visibleCount);

    if (isLoading) return <LoadingSpinner />;

    return (
        <div className="space-y-6">
            
            {/* Header & Actions */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 rounded-lg shadow-sm border border-gray-200 gap-4">
                <div>
                    <h3 className="text-2xl font-bold" style={{ color: BRAND_COLORS.darkBlue }}>The Wall of Fame</h3>
                    <p className="text-sm text-gray-500">Celebrate your colleagues' wins.</p>
                </div>
                <button 
                    onClick={() => setIsModalOpen(true)}
                    className="flex items-center px-6 py-3 text-white font-semibold rounded-lg hover:opacity-90 shadow-md transition transform hover:-translate-y-0.5"
                    style={{ backgroundColor: BRAND_COLORS.teal }}
                >
                    <PlusIcon className="h-5 w-5 mr-2" /> Give Recognition
                </button>
            </div>

            {/* Filters Bar */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <input 
                    type="text" 
                    placeholder="Search people or messages..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="p-2 border rounded-md focus:ring-2 outline-none w-full"
                    style={{ focusRingColor: BRAND_COLORS.teal }}
                />
                <select 
                    value={timeFilter} 
                    onChange={(e) => setTimeFilter(e.target.value)}
                    className="p-2 border rounded-md outline-none w-full"
                >
                    <option value="All">All Time</option>
                    <option value="Today">Today</option>
                    <option value="This Week">This Week</option>
                    <option value="This Month">This Month</option>
                    <option value="This Year">This Year</option>
                </select>
                <select 
                    value={valueFilter} 
                    onChange={(e) => setValueFilter(e.target.value)}
                    className="p-2 border rounded-md outline-none w-full"
                >
                    <option value="All">All Values</option>
                    <option value="commitment">Commitment</option>
                    <option value="collaboration">Collaboration</option>
                    <option value="customer">Customer Orientation</option>
                </select>
            </div>

            {/* The Feed */}
            <div className="space-y-4">
                {visibleAppreciations.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-lg border border-dashed border-gray-300">
                        <p className="text-gray-400">No appreciations found matching your filters.</p>
                    </div>
                ) : (
                    visibleAppreciations.map(item => (
                        <AppreciationCard 
                            key={item.id} 
                            data={item} 
                            userMap={userMap}
                            currentUser={userProfile}
                            appId={appId}
                            onUserClick={(uid) => setViewingUser(uid)}
                        />
                    ))
                )}
            </div>

            {/* Pagination */}
            {visibleCount < filteredAppreciations.length && (
                <div className="text-center pt-4">
                    <button 
                        onClick={() => setVisibleCount(prev => prev + 10)}
                        className="px-6 py-2 bg-white border border-gray-300 text-gray-600 rounded-full hover:bg-gray-50 font-medium transition shadow-sm"
                    >
                        Load More ({filteredAppreciations.length - visibleCount} remaining)
                    </button>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <GiveRecognitionModal 
                    appId={appId}
                    currentUser={userProfile}
                    users={users}
                    onClose={() => setIsModalOpen(false)}
                />
            )}

            {/* Achievement Card Modal */}
            {viewingUser && (
                <AchievementCard 
                    userId={viewingUser}
                    appId={appId}
                    onClose={() => setViewingUser(null)}
                />
            )}
        </div>
    );
}

// ===================================================================
// ## SUB-COMPONENTS
// ===================================================================

function AppreciationCard({ data, userMap, currentUser, appId, onUserClick }) {
    const valueConfig = VALUES[data.valueCategory] || VALUES['commitment'];
    const dateTime = formatDateTime(data.createdAt);
    const targetId = data.toUserId || data.toUserID || data.touserid;
    const recipientUser = userMap[targetId];
    const photoUrl = recipientUser?.photoURL;
    const myId = currentUser.uid || currentUser.id;

    const handleReaction = async (reactionType) => {
        try {
            const docRef = doc(db, `artifacts/${appId}/appreciations`, data.id);
            const currentReactions = data.reactions?.[reactionType] || [];
            
            if (currentReactions.includes(myId)) {
                await updateDoc(docRef, { [`reactions.${reactionType}`]: arrayRemove(myId) });
            } else {
                await updateDoc(docRef, { [`reactions.${reactionType}`]: arrayUnion(myId) });
            }
        } catch (error) { console.error("Failed to update reaction", error); }
    };

    const canDelete = currentUser.role === 'Admin' || currentUser.department === 'HR';
    const handleDelete = async () => {
        if (!window.confirm("Are you sure? This will delete the post AND deduct points.")) return;
        try {
            const batch = writeBatch(db);
            const appRef = doc(db, `artifacts/${appId}/appreciations`, data.id);
            batch.delete(appRef);
            if (targetId) {
                const userRef = doc(db, `artifacts/${appId}/users`, targetId);
                const pointsToRemove = data.pointsAwarded || 10;
                const badgeKey = data.valueCategory; 
                batch.update(userRef, { points: increment(-pointsToRemove), [`badges.${badgeKey}`]: increment(-1) });
            }
            await batch.commit();
        } catch (error) { alert("Failed to delete."); }
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition duration-200 animate-fadeIn relative group">
            {canDelete && (
                <button onClick={handleDelete} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full shadow-sm border border-gray-100 z-10">
                    <TrashIcon className="h-5 w-5" />
                </button>
            )}
            <div className="flex items-start">
                <div onClick={() => targetId && onUserClick(targetId)} className="cursor-pointer transition transform hover:scale-105" title="View Player Card">
                    <UserAvatar name={data.toUserName} photoUrl={photoUrl} />
                </div>
                <div className="ml-4 flex-grow">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start">
                        <div>
                            <p className="text-lg text-gray-800">
                                <span className="font-bold" style={{ color: BRAND_COLORS.darkBlue }}>{data.toUserName}</span> 
                                <span className="text-gray-500 font-light mx-2">was recognized by</span> 
                                <span className="font-medium text-gray-700">{data.fromUserName}</span>
                            </p>
                            <p className="text-xs text-gray-400 mt-1 font-mono flex items-center">{dateTime}</p>
                        </div>
                        
                        {/* ✅ CHALLENGE TAG */}
                        {data.challengeTitle && (
                            <div className="mt-2 md:mt-0 flex items-center px-2 py-1 bg-yellow-50 border border-yellow-200 rounded text-[10px] font-bold text-yellow-700 uppercase tracking-wide shadow-sm">
                                <TrophyIcon className="h-3 w-3 mr-1" />
                                {data.challengeTitle}
                            </div>
                        )}
                    </div>

                    <div className="mt-3 p-4 rounded-lg border border-l-4 bg-gray-50" style={{ borderLeftColor: valueConfig.color, borderColor: '#e5e7eb' }}>
                        <p className="text-gray-700 italic text-base">"{data.message}"</p>
                    </div>
                    
                    <div className="mt-4 flex flex-wrap justify-between items-center gap-4">
                        <div className="flex flex-wrap gap-2">
                            <span className="px-3 py-1 text-xs font-bold uppercase tracking-wide rounded-full border flex items-center shadow-sm" style={{ color: valueConfig.color, borderColor: valueConfig.color, backgroundColor: 'white' }}>
                                <span className="mr-1">★</span> {valueConfig.label}
                            </span>
                            <span className="px-3 py-1 text-xs font-medium rounded-full border bg-gray-100 text-gray-600 border-gray-200">{data.subValue}</span>
                        </div>
                        <div className="flex space-x-2">
                            {REACTIONS.map((r) => {
                                const userList = data.reactions?.[r.icon] || [];
                                const count = userList.length;
                                const hasReacted = userList.includes(myId);
                                return (
                                    <button key={r.icon} onClick={() => handleReaction(r.icon)} className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium transition-all ${hasReacted ? 'bg-blue-50 border border-blue-200 shadow-inner' : 'bg-transparent border border-transparent hover:bg-gray-50'}`}>
                                        <span className="text-base leading-none">{r.icon}</span>
                                        {count > 0 && <span className={`${hasReacted ? 'text-blue-600' : 'text-gray-500'}`}>{count}</span>}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// --- UPDATED MODAL WITH AUTOCOMPLETE & CHALLENGES ---
function GiveRecognitionModal({ appId, currentUser, users, onClose }) {
    const [selectedUser, setSelectedUser] = useState(null); 
    const [searchValue, setSearchValue] = useState('');     
    const [showDropdown, setShowDropdown] = useState(false);
    
    const [selectedValue, setSelectedValue] = useState('');
    const [selectedSubValue, setSelectedSubValue] = useState('');
    const [message, setMessage] = useState('');
    
    // ✅ NEW STATE: Challenges
    const [activeChallenges, setActiveChallenges] = useState([]);
    const [selectedChallengeId, setSelectedChallengeId] = useState('');
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const dropdownRef = useRef(null);

    // 1. Fetch Active Challenges
    useEffect(() => {
        const fetchActiveChallenges = async () => {
            try {
                const q = query(
                    collection(db, `artifacts/${appId}/challenges`),
                    where("status", "==", "Active")
                );
                const snap = await getDocs(q);
                setActiveChallenges(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (error) {
                console.error("Error fetching challenges", error);
            }
        };
        fetchActiveChallenges();
    }, [appId]);

    // 2. Autocomplete Filter
    const availableColleagues = useMemo(() => {
        return users.filter(u => 
            u.uid !== currentUser.uid && 
            u.id !== currentUser.uid &&
            u.id !== currentUser.id 
        );
    }, [users, currentUser]);

    const filteredUsers = useMemo(() => {
        if (!searchValue) return [];
        const lowerSearch = searchValue.toLowerCase();
        return availableColleagues.filter(u => 
            u.name?.toLowerCase().includes(lowerSearch) ||
            u.department?.toLowerCase().includes(lowerSearch) ||
            u.email?.toLowerCase().includes(lowerSearch)
        ).slice(0, 5); 
    }, [searchValue, availableColleagues]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [dropdownRef]);

    const triggerConfetti = () => {
        const duration = 3000;
        const animationEnd = Date.now() + duration;
        const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };
        const randomInRange = (min, max) => Math.random() * (max - min) + min;
        const interval = setInterval(() => {
            const timeLeft = animationEnd - Date.now();
            if (timeLeft <= 0) return clearInterval(interval);
            const particleCount = 50 * (timeLeft / duration);
            const colors = [BRAND_COLORS.teal, BRAND_COLORS.orange, BRAND_COLORS.red, BRAND_COLORS.darkBlue];
            confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }, colors });
            confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }, colors });
        }, 250);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedUser || !selectedValue || !selectedSubValue || !message) return;

        setIsSubmitting(true);
        try {
            // ✅ Challenge Data
            let challengeData = {};
            if (selectedChallengeId) {
                const chall = activeChallenges.find(c => c.id === selectedChallengeId);
                if (chall) {
                    challengeData = {
                        challengeId: chall.id,
                        challengeTitle: chall.title
                    };
                }
            }

            await addDoc(collection(db, `artifacts/${appId}/appreciations`), {
                fromUserId: currentUser.uid || currentUser.id,
                fromUserName: currentUser.name,
                toUserId: selectedUser.id, 
                toUserName: selectedUser.name,
                valueCategory: selectedValue,
                subValue: selectedSubValue,
                message: message,
                createdAt: new Date(),
                pointsAwarded: 10,
                reactions: {},
                ...challengeData // Save Tag
            });

            const receiverRef = doc(db, `artifacts/${appId}/users`, selectedUser.id);
            await updateDoc(receiverRef, {
                points: increment(10),
                [`badges.${selectedValue}`]: increment(1) 
            });

            onClose();
            triggerConfetti(); 
        } catch (error) {
            console.error("Error sending recognition:", error);
            alert("Failed to send recognition.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                <div className="p-6 flex justify-between items-center" style={{ backgroundColor: BRAND_COLORS.darkBlue }}>
                    <h3 className="text-white font-bold text-xl flex items-center">
                        <SparklesIcon className="h-6 w-6 mr-2" style={{ color: BRAND_COLORS.teal }} />
                        Give Recognition
                    </h3>
                    <button onClick={onClose} className="text-white hover:text-gray-300">
                        <XCircleIcon className="h-6 w-6" />
                    </button>
                </div>
                <div className="p-6 overflow-y-auto min-h-[400px]">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        
                        {/* --- AUTOCOMPLETE USER SEARCH --- */}
                        <div className="relative" ref={dropdownRef}>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Recognize a Colleague</label>
                            
                            {selectedUser ? (
                                <div className="flex items-center justify-between p-3 border border-green-200 bg-green-50 rounded-lg">
                                    <div className="flex items-center">
                                        <UserAvatar name={selectedUser.name} photoUrl={selectedUser.photoURL} size="sm" />
                                        <div className="ml-3">
                                            <p className="text-sm font-bold text-gray-800">{selectedUser.name}</p>
                                            <p className="text-xs text-gray-500">{selectedUser.department}</p>
                                        </div>
                                    </div>
                                    <button 
                                        type="button" 
                                        onClick={() => { setSelectedUser(null); setSearchValue(''); }}
                                        className="text-gray-400 hover:text-red-500"
                                    >
                                        <XCircleIcon className="h-5 w-5" />
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <input 
                                        type="text" 
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 outline-none"
                                        placeholder="Type a name (e.g. Sarah)..."
                                        value={searchValue}
                                        onChange={(e) => {
                                            setSearchValue(e.target.value);
                                            setShowDropdown(true);
                                        }}
                                        onFocus={() => setShowDropdown(true)}
                                    />
                                    {showDropdown && searchValue && (
                                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                            {filteredUsers.length > 0 ? (
                                                filteredUsers.map(u => (
                                                    <div 
                                                        key={u.id}
                                                        onClick={() => {
                                                            setSelectedUser(u);
                                                            setShowDropdown(false);
                                                            setSearchValue('');
                                                        }}
                                                        className="flex items-center p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0 transition"
                                                    >
                                                        <UserAvatar name={u.name} photoUrl={u.photoURL} size="sm" />
                                                        <div className="ml-3">
                                                            <p className="text-sm font-medium text-gray-800">{u.name}</p>
                                                            <p className="text-xs text-gray-500">{u.department} • {u.designation || 'Employee'}</p>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="p-3 text-sm text-gray-400 text-center">No matches found.</div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* ✅ CHALLENGE SELECTOR */}
                        {activeChallenges.length > 0 && (
                            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                                <label className="block text-sm font-bold text-yellow-800 mb-2 flex items-center">
                                    <TrophyIcon className="h-4 w-4 mr-1"/> 
                                    Is this for a Challenge?
                                </label>
                                <select 
                                    value={selectedChallengeId} 
                                    onChange={(e) => setSelectedChallengeId(e.target.value)}
                                    className="w-full p-2 border border-yellow-300 rounded bg-white text-sm focus:ring-2 focus:ring-yellow-400 outline-none"
                                >
                                    <option value="">No, General Recognition</option>
                                    {activeChallenges.map(c => (
                                        <option key={c.id} value={c.id}>
                                            🏆 {c.title} (Goal: {c.goal} pts)
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-3">Select a Value</label>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {Object.entries(VALUES).map(([key, val]) => (
                                    <button key={key} type="button" onClick={() => { setSelectedValue(key); setSelectedSubValue(''); }} className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all duration-200 ${selectedValue === key ? 'shadow-lg transform scale-105' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} style={{ borderColor: selectedValue === key ? val.color : '', backgroundColor: selectedValue === key ? val.bg : '' }}>
                                        <div style={{ color: val.color }}>{val.icon}</div>
                                        <h4 className="font-bold text-sm" style={{ color: val.color }}>{val.label}</h4>
                                        <p className="text-xs text-gray-500 mt-2 text-center leading-tight">{val.description}</p>
                                    </button>
                                ))}
                            </div>
                        </div>
                        {selectedValue && (
                            <div className="animate-fadeIn">
                                <label className="block text-sm font-bold text-gray-700 mb-2">Specific Behavior</label>
                                <select required value={selectedSubValue} onChange={(e) => setSelectedSubValue(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50">
                                    <option value="">-- Select Specific Behavior --</option>
                                    {VALUES[selectedValue].subValues.map(sv => <option key={sv} value={sv}>{sv}</option>)}
                                </select>
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Appreciation Note</label>
                            <textarea required rows="3" value={message} onChange={(e) => setMessage(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 outline-none" placeholder="Why are you recognizing them?" />
                        </div>
                        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                            <button type="button" onClick={onClose} className="px-6 py-3 text-gray-600 hover:bg-gray-100 rounded-lg font-medium">Cancel</button>
                            <button type="submit" disabled={isSubmitting} className="px-8 py-3 text-white font-bold rounded-lg shadow-md hover:shadow-lg transition transform hover:-translate-y-0.5" style={{ backgroundColor: BRAND_COLORS.teal }}>{isSubmitting ? 'Sending...' : 'Send Recognition'}</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}